import Cookies from "js-cookie";

/**
 * Filter an array of activities based on the current user's permitted/assigned workflows.
 *
 * Checks `workflowList` (or fallback keys `activityList`, `activities`, `workflows`)
 * on `userDetails` from `localStorage` (or passed `userDetailsOverride`).
 *
 * Rules:
 * 1. If activityList is not an array or empty, returns [].
 * 2. If user is super_admin or admin and has NO assigned workflow list (undefined/null or isAllActivity),
 *    returns all activities for the project.
 * 3. If user has a non-empty assigned workflow list (any role including admin/super_admin/office_user/field_agent),
 *    filters to ONLY those matching assigned IDs.
 * 4. If non-admin user has an empty assigned list (`[]`), returns [] (restricted access).
 * 5. Handles all ID formats: numbers, strings, and objects (`{ workflowId }` or `{ id }`).
 *
 * @param {Array} activityList - Array of activity objects
 * @param {Object} [userDetailsOverride] - Optional userDetails object for testing or explicit scoping
 * @returns {Array} Filtered activity list
 */
export const getPermittedActivities = (activityList, userDetailsOverride = null) => {
  if (!Array.isArray(activityList) || activityList.length === 0) {
    return [];
  }

  let userDetails = userDetailsOverride;
  if (!userDetails) {
    try {
      userDetails = JSON.parse(localStorage.getItem("userDetails") || "{}");
    } catch {
      userDetails = {};
    }
  }

  const userType = userDetails?.userType || Cookies.get("userType") || "";
  const assignedList =
    userDetails?.workflowList ??
    userDetails?.activityList ??
    userDetails?.activities ??
    userDetails?.workflows;

  // If user explicitly has an assigned list
  if (Array.isArray(assignedList)) {
    // If empty array:
    // For non-admin roles, an empty list means no assigned activities
    if (assignedList.length === 0) {
      if (userType === "super_admin" || userType === "admin") {
        return activityList;
      }
      return [];
    }

    const permittedIdSet = new Set(
      assignedList
        .map((item) => {
          if (typeof item === "object" && item !== null) {
            return String(item.workflowId ?? item.id ?? item.value ?? "");
          }
          return String(item);
        })
        .filter(Boolean)
    );

    return activityList.filter((act) => {
      const actId = String(act?.workflowId ?? act?.id ?? act?.value ?? "");
      return permittedIdSet.has(actId);
    });
  }

  // If no assigned workflowList is present at all:
  // super_admin and admin see all activities; other roles default to activityList if unrestricted
  return activityList;
};

export default getPermittedActivities;
